import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  IconButton,
  TooltipHost,
} from "@fluentui/react";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { StyledPrimaryButton } from "components/styled";
import { useCancelVehicleFlottomatBusinessCase } from "hooks/data/mutations/useCancelVehicleFlottomatBusinessCase";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { setVehicle } from "../../store/vehicle";
import { Vehicle } from "../../types/types";

/**
 * Cancels the pending Flottomat business case of the vehicle (e.g. Generali did not issue the policy for a long time),
 * so BFM no longer checks it with Generali. The caller decides when it is shown (internal users with write access) and,
 * with disabledReason, when it is disabled (shown as tooltip).
 */
const CancelFlottomatBusinessCaseButton = ({
  vehicle,
  disabledReason,
}: {
  vehicle: Vehicle;
  disabledReason?: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;
  const dispatch = useDispatch();
  const phraseActivated = usePhraseActivationStatus().activated;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutate: cancelBusinessCase, isLoading } =
    useCancelVehicleFlottomatBusinessCase();

  const close = () => setIsDialogOpen(false);

  const onCancelBusinessCase = () => {
    cancelBusinessCase(
      {
        fleetId: String(vehicle.fleetId),
        vehicleId: String(vehicle.vehicleId),
        businessCaseNumber: vehicle.flottomatBusinessCaseNumber,
      },
      {
        onSuccess: (res) => {
          dispatch(setVehicle(res.data));
          toast.success(t("bfm.success"));
          close();
        },
      },
    );
  };

  return (
    <>
      <TooltipHost
        content={disabledReason ?? t("bfm.flottomatBusinessCase.cancel.label")}
      >
        {/* allowDisabledFocus: a disabled button still gets the mouse events, so the tooltip shows the reason */}
        <IconButton
          iconProps={{ iconName: "Cancel" }}
          style={disabledReason ? undefined : { color: theme.palette.redDark }}
          disabled={isLoading || !!disabledReason}
          allowDisabledFocus
          onClick={() => setIsDialogOpen(true)}
        />
      </TooltipHost>
      {isDialogOpen && (
        <Dialog
          hidden={false}
          onDismiss={close}
          modalProps={{
            isBlocking: phraseActivated ? false : true,
          }}
          dialogContentProps={{
            type: DialogType.normal,
            title: (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Icon
                  iconName="Warning"
                  style={{
                    color: theme.semanticColors.errorIcon,
                    fontSize: "25px",
                    marginRight: "5px",
                  }}
                />
                {t("bfm.flottomatBusinessCase.cancel.label")}
              </div>
            ),
            subText: t("bfm.flottomatBusinessCase.cancelConfirm.label", {
              number: vehicle.flottomatBusinessCaseNumber,
            }),
          }}
        >
          <DialogFooter>
            <StyledPrimaryButton
              text={t("bfm.flottomatBusinessCase.cancel.label")}
              disabled={isLoading}
              onClick={onCancelBusinessCase}
            />
            <DefaultButton onClick={close} text={t("bfm.close.label")} />
          </DialogFooter>
          {isLoading && <FormSpinner />}
        </Dialog>
      )}
    </>
  );
};

export default CancelFlottomatBusinessCaseButton;
